import { unsafeCSS, html } from 'lit';
import { property, state } from 'lit/decorators.js';

import { PopBaseComponent } from '../../../base/pop-base-component';
import {
  Product,
  SortDirection,
  TableRecordVariant,
  type CurrentSort,
} from '../../product-center-table-wrapper/product-center-table-wrapper.model';
import { Constants as ProductCenterTableWrapperConstants } from '../../product-center-table-wrapper/product-center-table-wrapper.constants';
import { Constants } from './performance-table.constants';
import { customFormatDate } from '../../../common/utils/formatDate';
import { formatToNDecimalPlaces } from '../../../common/utils/formatNumber';
import { getCurrencySymbol } from '../../../common/utils/currency';
import { buildShareClassDescriptor } from '../../../common/utils/buildShareClass';
import { zustandStateToComponent } from '../../store/product-center.store';
import { TableTypeEnum } from '../../store/product-center.store.model';
import {
  sortDataCells,
  sortProductDataCells,
  validateIfProductHeaderSort,
} from '../common/helper/table-helper';
import {
  checkProductCallback,
  getAPCDateFormat,
  handleAdvancedSortByDropdownChange,
  handleSortProductDataCells,
  handleUpdate,
  renderProductCellData,
  renderProductHeader,
  renderTableCells,
  renderTableHeader,
  sortDataCellsCallBack,
} from '../../product-center-table-wrapper/product-center-table-wrapper.utils';
import Locale from '../../../common/utils/locale.js';

import style from './performance-table.scss?inline';
/**
 * @tag pop-pc-performance-table-component
 * @property {ProductCenterExhibit} [componentObject] Performance Table content
 * @attribute {ProductCenterExhibit} [data-component-object] Performance Table content
 */
export class PopPerformanceTableComponent extends PopBaseComponent {
  static get styles() {
    return [...super.styles, unsafeCSS(style)];
  }

  static readonly tagName = Constants.TAG_NAME;

  /**
   * @internal
   */
  @property()
  private filteredProducts: Product[] = [];

  /**
   * @internal
   */
  @property()
  private sortByDropdownValue =
    ProductCenterTableWrapperConstants.DEFAULT_SORT_BY_DROP_DOWN_OPTION.value;

  /**
   * @internal
   */
  @property()
  selectedProducts: Product['productCode'][] = [];

  @state()
  public sortDirectionState: SortDirection = SortDirection.ASCENDING;

  @property()
  tableHeaders: NodeListOf<Element> | undefined;

  private currentSort?: CurrentSort;

  /*
   * Component Methods
   */

  constructor() {
    super();
    this.enableI18n = true;
  }

  update(changedProperties: Map<string, unknown>) {
    handleUpdate(this, changedProperties);
    super.update(changedProperties);
  }

  private sortProductDataCells(e: CustomEvent) {
    handleSortProductDataCells(this, e);
  }

  private sortByDropdownChangeHandler(e: CustomEvent) {
    this.sortByDropdownValue = handleAdvancedSortByDropdownChange(
      e,
      ProductCenterTableWrapperConstants.DEFAULT_SORT_BY_DROP_DOWN_OPTION.value,
      this.tableHeaders,
      this.filteredProducts,
      ProductCenterTableWrapperConstants.SORT_BUTTON_ID,
      this.shadowRoot,
      this.requestUpdate.bind(this),
      (sort) => {
        this.currentSort = sort;
      },
      (products) => {
        this.filteredProducts = products;
      },
    );
    if (this.currentSort) {
      this.currentSort.columnId = undefined;
    }
  }

  private getAsOfDate(): string {
    let asOfDate = '';

    if (this.filteredProducts) {
      for (const product of this.filteredProducts) {
        if (product.performance && product.performance.performanceAsOfDate) {
          asOfDate = product.performance.performanceAsOfDate;
          break;
        }
      }
    }

    return asOfDate;
  }

  private renderTableHead() {
    return html`
      <tr>
        ${renderProductHeader(
          {
            rowspan: 2,
            sortByDropdownValue: this.sortByDropdownValue,
            sortByCallBack: this.sortByDropdownChangeHandler,
            onSort: this.sortProductDataCells,
            tableType: TableTypeEnum.PERFORMANCE,
          },
          new Locale(this.language, this.country),
        )}
        <th
          scope="col"
          colspan="6"
          id="${Constants.PERFORMANCE_COLUMN_IDS.PERFORMANCE_TABLE}"
        >
          <div class="b-flex b-justify-between pop-performance-header">
            <span class="pop-performance-title"
              >${this.localize(Constants.TRANSLATION_KEYS.HEADERS.TABLE_NAME)}
              %</span
            >
            <span class="b-typography-sans-serif--caption--100"
              >${this.localize('as_of_date', {
                date: this.getAsOfDate(),
              })}</span
            >
          </div>
        </th>
        ${renderTableHeader({
          columnId: Constants.PERFORMANCE_COLUMN_IDS.INCEPTION_DATE,
          headerName: this.localize(
            Constants.TRANSLATION_KEYS.HEADERS.INCEPTION_DATE,
          ),
          showAnnualised: false,
          colspan: 1,
          rowspan: 2,
          parentHeader: Constants.PERFORMANCE_COLUMN_IDS.PERFORMANCE_TABLE,
          sortable: true,
          onSort: (e: CustomEvent) =>
            sortDataCellsCallBack(
              this,
              e,
              Constants.PERFORMANCE_COLUMN_IDS.INCEPTION_DATE,
            ),
        })}
      </tr>
      <tr>
        ${renderTableHeader({
          columnId: Constants.PERFORMANCE_COLUMN_IDS.YTD,
          headerName: this.localize(Constants.TRANSLATION_KEYS.HEADERS.YTD),
          showAnnualised: false,
          colspan: 1,
          rowspan: 1,
          parentHeader: Constants.PERFORMANCE_COLUMN_IDS.PERFORMANCE_TABLE,
          sortable: true,
          onSort: (e: CustomEvent) =>
            sortDataCellsCallBack(
              this,
              e,
              Constants.PERFORMANCE_COLUMN_IDS.YTD,
            ),
        })}
        ${renderTableHeader({
          columnId: Constants.PERFORMANCE_COLUMN_IDS.ONE_YEAR,
          headerName: this.localize(
            Constants.TRANSLATION_KEYS.HEADERS.ONE_YEAR,
          ),
          showAnnualised: false,
          colspan: 1,
          rowspan: 1,
          parentHeader: Constants.PERFORMANCE_COLUMN_IDS.PERFORMANCE_TABLE,
          sortable: true,
          onSort: (e: CustomEvent) =>
            sortDataCellsCallBack(
              this,
              e,
              Constants.PERFORMANCE_COLUMN_IDS.ONE_YEAR,
            ),
        })}
        ${renderTableHeader({
          columnId: Constants.PERFORMANCE_COLUMN_IDS.THREE_YEARS,
          headerName: this.localize(
            Constants.TRANSLATION_KEYS.HEADERS.THREE_YEARS,
          ),
          showAnnualised: true,
          colspan: 1,
          rowspan: 1,
          parentHeader: Constants.PERFORMANCE_COLUMN_IDS.PERFORMANCE_TABLE,
          sortable: true,
          onSort: (e: CustomEvent) =>
            sortDataCellsCallBack(
              this,
              e,
              Constants.PERFORMANCE_COLUMN_IDS.THREE_YEARS,
            ),
          annualised: this.localize(
            ProductCenterTableWrapperConstants.TRANSLATION_KEYS.ANNUALISED,
          ),
        })}
        ${renderTableHeader({
          columnId: Constants.PERFORMANCE_COLUMN_IDS.FIVE_YEARS,
          headerName: this.localize(
            Constants.TRANSLATION_KEYS.HEADERS.FIVE_YEARS,
          ),
          showAnnualised: true,
          colspan: 1,
          rowspan: 1,
          parentHeader: Constants.PERFORMANCE_COLUMN_IDS.PERFORMANCE_TABLE,
          sortable: true,
          onSort: (e: CustomEvent) =>
            sortDataCellsCallBack(
              this,
              e,
              Constants.PERFORMANCE_COLUMN_IDS.FIVE_YEARS,
            ),
          annualised: this.localize(
            ProductCenterTableWrapperConstants.TRANSLATION_KEYS.ANNUALISED,
          ),
        })}
        ${renderTableHeader({
          columnId: Constants.PERFORMANCE_COLUMN_IDS.TEN_YEARS,
          headerName: this.localize(
            Constants.TRANSLATION_KEYS.HEADERS.TEN_YEARS,
          ),
          showAnnualised: true,
          colspan: 1,
          rowspan: 1,
          parentHeader: Constants.PERFORMANCE_COLUMN_IDS.PERFORMANCE_TABLE,
          sortable: true,
          onSort: (e: CustomEvent) =>
            sortDataCellsCallBack(
              this,
              e,
              Constants.PERFORMANCE_COLUMN_IDS.TEN_YEARS,
            ),
        })}
        ${renderTableHeader({
          columnId: Constants.PERFORMANCE_COLUMN_IDS.SINCE_INCEPTION,
          headerName: this.localize(
            Constants.TRANSLATION_KEYS.HEADERS.SINCE_INCEPTION,
          ),
          showAnnualised: true,
          colspan: 1,
          rowspan: 1,
          parentHeader: Constants.PERFORMANCE_COLUMN_IDS.PERFORMANCE_TABLE,
          sortable: true,
          onSort: (e: CustomEvent) =>
            sortDataCellsCallBack(
              this,
              e,
              Constants.PERFORMANCE_COLUMN_IDS.SINCE_INCEPTION,
            ),
          annualised: this.localize(
            ProductCenterTableWrapperConstants.TRANSLATION_KEYS.ANNUALISED,
          ),
        })}
      </tr>
    `;
  }

  private renderTableBody() {
    if (this.currentSort) {
      if (validateIfProductHeaderSort(this.currentSort.columnId)) {
        this.filteredProducts = sortProductDataCells(
          this.currentSort.tableHeaderEl,
          this.filteredProducts,
          this.currentSort.columnId,
        );
      } else {
        this.filteredProducts = sortDataCells(
          this.currentSort.tableHeaderEl,
          this.filteredProducts,
          this.currentSort.columnId,
        );
      }
    }

    return html`
      ${this.filteredProducts.map((product: Product) => {
        return html`
          <tr>
            ${renderProductCellData({
              index: JSON.stringify(product.productName),
              sortValue: product.productName[this.sortByDropdownValue] ?? '',
              checkboxLabel: product.productName.vehicleTypeName,
              fundName: product.productName.fundName,
              shareClass: product.productName.shareClass
                ? buildShareClassDescriptor(product.productName.shareClass)
                : '',
              currencySymbol: getCurrencySymbol(
                product.productName.baseCurrencyName,
              ),
              currencyName: product.productName.baseCurrencyName,
              apir: product.productName.apir ?? '',
              productCode: product.productCode,
              checkProductCallBack: (event: Event) =>
                checkProductCallback(event, TableTypeEnum.PERFORMANCE),
              isChecked: this.selectedProducts.includes(product.productCode),
              fundLink: product.productName.fundLink,
              showPopCheckbox: true,
              disclaimers: product.disclaimers ?? undefined,
              apirLabel: this.localize(
                ProductCenterTableWrapperConstants.TRANSLATION_KEYS.APIR,
              ),
              noticeLabel: this.localize(
                ProductCenterTableWrapperConstants.TRANSLATION_KEYS.NOTICE,
              ),
            })}
            ${renderTableCells({
              sortValue: `${product.performance?.fundReturns?.yearToDate?.[0]?.value}`,
              variant: TableRecordVariant.NUMERAL,
              cellClass: '',
              value: html`${formatToNDecimalPlaces(
                product.performance?.fundReturns?.yearToDate?.[0]?.value,
                2,
                ProductCenterTableWrapperConstants.CUSTOM_NO_VALUE_SYMBOL,
              )}`,
            })}
            ${renderTableCells({
              sortValue: `${product.performance?.fundReturns?.oneYear?.[0]?.value}`,
              variant: TableRecordVariant.NUMERAL,
              cellClass: '',
              value: html`${formatToNDecimalPlaces(
                product.performance?.fundReturns?.oneYear?.[0]?.value,
                2,
                ProductCenterTableWrapperConstants.CUSTOM_NO_VALUE_SYMBOL,
              )}`,
            })}
            ${renderTableCells({
              sortValue: `${product.performance?.fundReturns?.threeYears?.[0]?.value}`,
              variant: TableRecordVariant.NUMERAL,
              cellClass: '',
              value: html`${formatToNDecimalPlaces(
                product.performance?.fundReturns?.threeYears?.[0]?.value,
                2,
                ProductCenterTableWrapperConstants.CUSTOM_NO_VALUE_SYMBOL,
              )}`,
            })}
            ${renderTableCells({
              sortValue: `${product.performance?.fundReturns?.fiveYears?.[0]?.value}`,
              variant: TableRecordVariant.NUMERAL,
              cellClass: '',
              value: html`${formatToNDecimalPlaces(
                product.performance?.fundReturns?.fiveYears?.[0]?.value,
                2,
                ProductCenterTableWrapperConstants.CUSTOM_NO_VALUE_SYMBOL,
              )}`,
            })}
            ${renderTableCells({
              sortValue: `${product.performance?.fundReturns?.tenYears?.[0]?.value}`,
              variant: TableRecordVariant.NUMERAL,
              cellClass: '',
              value: html`${formatToNDecimalPlaces(
                product.performance?.fundReturns?.tenYears?.[0]?.value,
                2,
                ProductCenterTableWrapperConstants.CUSTOM_NO_VALUE_SYMBOL,
              )}`,
            })}
            ${renderTableCells({
              sortValue: `${
                product.performance?.fundReturns?.sinceInception?.[0]?.value
              }`,
              variant: TableRecordVariant.NUMERAL,
              cellClass: '',
              value: html`${formatToNDecimalPlaces(
                product.performance?.fundReturns?.sinceInception?.[0]?.value,
                2,
                ProductCenterTableWrapperConstants.CUSTOM_NO_VALUE_SYMBOL,
              )}`,
            })}
            ${renderTableCells({
              sortValue: `${+new Date(product.performance?.inceptionDate ?? '')}`,
              variant: TableRecordVariant.NUMERAL,
              cellClass: '',
              value: html`${getAPCDateFormat(
                customFormatDate(
                  product.performance?.inceptionDate,
                  ProductCenterTableWrapperConstants.DATE_FORMATS
                    .MEDIUM_DATE_FORMAT,
                ),
              )}`,
            })}
          </tr>
        `;
      })}
    `;
  }

  renderComponent() {
    return html` <beacon-table variant="fixed" density="standard">
      <table>
        <thead>
          ${this.renderTableHead()}
        </thead>
        <tbody>
          ${this.renderTableBody()}
        </tbody>
      </table>
    </beacon-table>`;
  }

  render() {
    if (
      (!this.filteredProducts && !this.componentObject) ||
      !this.isI18nLoaded
    ) {
      return html``;
    }
    return this.renderComponent();
  }
}

customElements.define(
  Constants.TAG_NAME,
  zustandStateToComponent(
    {
      mapState: (productCenterState) => {
        return {
          filteredProducts: productCenterState.getFilteredProducts(),
          selectedProducts:
            productCenterState.selectedProducts[TableTypeEnum.PERFORMANCE],
        };
      },
    },
    PopPerformanceTableComponent,
  ),
);
